import { useEffect, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { PageRoutes } from "../../../constants/routes";
import { Area, AreaStatus } from "../../../ducks/area";
import { useArea } from "../../../hooks/useArea";
import { AnalysisAreaSelectionItem } from "../../molecules/analysis/AnalysisAreaSelectionItem";
import { SubAreaSelectionPopup } from "../../molecules/area/SubAreaSelectionPopup";
import { useAnalysis } from "../../pages/analysis/hook";
import { AreaSelectionMap } from "../map/AreaSelectionMap";

export const SelectAnalysisArea = () => {
  const { selectedAnalysisItem, SelectedAnalysisItemIcon } = useAnalysis();
  const {
    allAreas,
    selectedAreas,
    selectedSubAreas,
    newArea,
    selectArea,
    removeArea,
    handleSelectNewArea,
    resetSelectedSubAreas,
  } = useArea();

  useEffect(() => {
    resetSelectedSubAreas();
  }, []);

  const filteredAreas = useMemo(() => {
    if (selectedAnalysisItem && selectedAnalysisItem.is_multi)
      return allAreas.filter((area) => area.for_multi);
    else return allAreas;
  }, [allAreas, selectedAnalysisItem]);

  if (!selectedAnalysisItem)
    return <Navigate to={PageRoutes.ANALYSIS_EXECUTE} />;

  const _renderPopup = (area: Area) => {
    if (area.subAreas.length > 0) {
      const filteredSelectedAreas = selectedAreas.filter(
        (a) =>
          a.id !== area.id && !area.subAreas.map((x) => x.id).includes(a.id),
      );

      const selectedAreaIds = selectedAreas.map((a) => a.id);
      if (
        selectedAreaIds.includes(area.id) ||
        area.subAreas.some((a) => selectedAreaIds.includes(a.id))
      ) {
        removeArea(area);
        area.subAreas.forEach((a) => removeArea(a));
      }

      const checkDisabled = (a: Area) => {
        if (
          filteredSelectedAreas.length > 0 &&
          a.dataset.group_id !== filteredSelectedAreas[0].dataset.group_id
        )
          return true;

        if (selectedAnalysisItem && selectedAnalysisItem.is_multi) {
          if (
            selectedAnalysisItem.max &&
            filteredSelectedAreas.length >= selectedAnalysisItem.max
          )
            return true;

          return a.status === AreaStatus.Failure || !a.for_multi;
        }

        if (selectedAnalysisItem && selectedAnalysisItem.max) {
          return (
            a.status === AreaStatus.Failure ||
            filteredSelectedAreas.length >= selectedAnalysisItem.max
          );
        }

        return a.status === AreaStatus.Failure;
      };

      return (
        <SubAreaSelectionPopup
          area={area}
          onSelect={(area, selected) => {
            if (selected) removeArea(area);
            else selectArea({ area });
          }}
          checkDisabled={checkDisabled}
          currentLength={filteredSelectedAreas.length}
          maxLength={selectedAnalysisItem.max}
        />
      );
    }

    return <></>;
  };

  return (
    <div className="flex flex-col flex-grow gap-y-5 overflow-hidden">
      <div className="flex items-center rounded-[40px]">
        {SelectedAnalysisItemIcon && <SelectedAnalysisItemIcon />}

        <div className="ml-[5px] text-[16px] font-bold text-primary">
          {selectedAnalysisItem.name}
        </div>
      </div>

      <div className="text-sm">
        分析するエリアを選択してください
        {selectedAnalysisItem.max
          ? `（${selectedAnalysisItem.max}個まで選択可）`
          : "（複数選択可）"}
      </div>

      <div className="flex gap-x-4 min-h-0 h-full">
        <div className="w-[40%] px-4 overflow-y-auto">
          {filteredAreas.map((area, i) => (
            <AnalysisAreaSelectionItem
              key={`select-analysis-item-${area.id}-${i}`}
              area={area}
              selectedAreas={selectedAreas}
              selectedSubAreas={selectedSubAreas}
              selectedAnalysisItem={selectedAnalysisItem}
              onSelectArea={handleSelectNewArea}
            />
          ))}
        </div>

        <AreaSelectionMap
          mode="area"
          newArea={newArea}
          renderPopup={_renderPopup}
        />
      </div>
    </div>
  );
};
